import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useAppTheme } from '@/theme/ThemeContext'
import {
  APIAlertTypeDetails,
  updateAlertTypeText,
} from '@/services/notificationService'

type Props = {
  open: boolean
  onClose: () => void
  siteId: string
  alertTypeId: number
  initialTitle: string
  initialDescription: string
  onSaved: (updated: APIAlertTypeDetails) => void
}

// Edit dialog for the card menu's "עריכת מלל וכותרת" action. Title + description
// only — severity has its own control elsewhere, so this never sends
// severityHint. Styling follows the other notification dialogs: the dark/light
// Dialog chrome from NewNotificationDialog, the inline-Alert + busy-disables-Save
// behavior from SendNotificationDialog. No Figma exists for this dialog.
const EditAlertTextDialog = ({
  open,
  onClose,
  siteId,
  alertTypeId,
  initialTitle,
  initialDescription,
  onSaved,
}: Props) => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [title, setTitle] = useState(initialTitle)
  const [description, setDescription] = useState(initialDescription)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-sync with the current card every time the dialog opens, so a reopen
  // after a successful edit (or after another edit elsewhere) never shows
  // stale text, and a cancelled edit never leaks into the next open.
  useEffect(() => {
    if (open) {
      setTitle(initialTitle)
      setDescription(initialDescription)
      setError(null)
    }
  }, [open, initialTitle, initialDescription])

  const colors = {
    dialogBg: isDarkMode ? '#162645' : '#FFFFFF',
    titleColor: isDarkMode ? '#fff' : '#0F3E66',
    textColor: isDarkMode ? '#fff' : undefined,
  }

  const trimmedTitle = title.trim()
  const canSave = !saving && trimmedTitle.length > 0

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      const updated = await updateAlertTypeText(siteId, alertTypeId, {
        alertTypeName: trimmedTitle,
        description,
      })
      onSaved(updated)
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        setError('שם זה תפוס על ידי התראה אחרת — בחרו שם אחר')
      } else {
        setError('השמירה נכשלה — נסו שוב')
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      dir="rtl"
      PaperProps={{
        sx: { borderRadius: '14px', bgcolor: colors.dialogBg },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: colors.titleColor,
          fontFamily: 'Rubik, sans-serif',
        }}
      >
        עריכת מלל וכותרת
        <IconButton
          onClick={onClose}
          aria-label="סגור"
          sx={{ position: 'absolute', left: 8, top: 8 }}
        >
          <CloseIcon sx={{ color: colors.titleColor }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="כותרת"
            value={title}
            onChange={e => setTitle(e.target.value)}
            error={trimmedTitle.length === 0}
            fullWidth
            autoFocus
            slotProps={{
              htmlInput: { dir: 'rtl' },
              inputLabel: { sx: { color: colors.textColor } },
            }}
            sx={{ '& .MuiInputBase-input': { color: colors.textColor } }}
          />
          <TextField
            label="תיאור"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            slotProps={{
              htmlInput: { dir: 'rtl' },
              inputLabel: { sx: { color: colors.textColor } },
            }}
            sx={{ '& .MuiInputBase-input': { color: colors.textColor } }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          ביטול
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditAlertTextDialog
